// Slot Symbols - Temática mafiosa Cosa Nostra
export type SlotSymbol = 'wild' | 'scatter' | 'gun' | 'dice' | 'cards' | 'diamond' | 'whiskey' | 'cigar'

export interface SymbolInfo {
  id: SlotSymbol
  emoji: string
  name: string
  isWild: boolean
  isScatter: boolean
}

export const SYMBOLS: Record<SlotSymbol, SymbolInfo> = {
  wild: { id: 'wild', emoji: '🎩', name: 'Wild', isWild: true, isScatter: false },
  scatter: { id: 'scatter', emoji: '💰', name: 'Scatter', isWild: false, isScatter: true },
  gun: { id: 'gun', emoji: '🔫', name: 'Pistola', isWild: false, isScatter: false },
  dice: { id: 'dice', emoji: '🎲', name: 'Dados', isWild: false, isScatter: false },
  cards: { id: 'cards', emoji: '🃏', name: 'Cartas', isWild: false, isScatter: false },
  diamond: { id: 'diamond', emoji: '💎', name: 'Diamante', isWild: false, isScatter: false },
  whiskey: { id: 'whiskey', emoji: '🥃', name: 'Whiskey', isWild: false, isScatter: false },
  cigar: { id: 'cigar', emoji: '🚬', name: 'Cigarro', isWild: false, isScatter: false },
}

// Tabla de pagos: índice = cantidad de símbolos - 1 (ej: índice 2 = 3 símbolos)
// [x1, x2, x3, x4, x5] donde x1 y x2 son 0 porque mínimo 3 para ganar
export const PAY_TABLE: Record<SlotSymbol, number[]> = {
  wild: [0, 0, 10, 50, 100],    // 🎩 x3=10, x4=50, x5=100
  scatter: [0, 0, 5, 20, 50],   // 💰 x3=5, x4=20, x5=50 (+ free spins)
  gun: [0, 0, 4, 12, 25],       // 🔫
  dice: [0, 0, 3, 10, 20],      // 🎲
  cards: [0, 0, 2, 8, 15],      // 🃏
  diamond: [0, 0, 2, 5, 10],    // 💎
  whiskey: [0, 0, 1, 3, 5],     // 🥃
  cigar: [0, 0, 1, 2, 3],       // 🚬
}

// Free spins por cantidad de scatters
export const FREE_SPINS_TABLE: Record<number, number> = {
  3: 5,   // 3 scatters = 5 free spins
  4: 10,  // 4 scatters = 10 free spins
  5: 15,  // 5 scatters = 15 free spins
}

// Pesos para la generación aleatoria (mayor peso = más frecuente)
export const SYMBOL_WEIGHTS: Record<SlotSymbol, number> = {
  wild: 2,      // Muy raro
  scatter: 3,   // Raro
  gun: 8,
  dice: 10,
  cards: 12,
  diamond: 15,
  whiskey: 20,
  cigar: 30,    // Más común
}

// 10 líneas de pago: [reel, row] donde reel=0-4, row=0-2
export const PAYLINES: [number, number][][] = [
  // Línea 1: Centro horizontal
  [[0, 1], [1, 1], [2, 1], [3, 1], [4, 1]],
  // Línea 2: Arriba horizontal
  [[0, 0], [1, 0], [2, 0], [3, 0], [4, 0]],
  // Línea 3: Abajo horizontal
  [[0, 2], [1, 2], [2, 2], [3, 2], [4, 2]],
  // Línea 4: V diagonal (arriba-centro-abajo-centro-arriba)
  [[0, 0], [1, 1], [2, 2], [3, 1], [4, 0]],
  // Línea 5: V invertida (abajo-centro-arriba-centro-abajo)
  [[0, 2], [1, 1], [2, 0], [3, 1], [4, 2]],
  // Línea 6: Zigzag arriba
  [[0, 0], [1, 1], [2, 0], [3, 1], [4, 0]],
  // Línea 7: Zigzag abajo
  [[0, 2], [1, 1], [2, 2], [3, 1], [4, 2]],
  // Línea 8: Diagonal descendente (arriba-izq a abajo-der)
  [[0, 0], [1, 0], [2, 1], [3, 2], [4, 2]],
  // Línea 9: Diagonal ascendente (abajo-izq a arriba-der)
  [[0, 2], [1, 2], [2, 1], [3, 0], [4, 0]],
  // Línea 10: Centro con picos
  [[0, 1], [1, 0], [2, 1], [3, 0], [4, 1]],
]

// Colores para cada línea de pago (para visualización)
export const PAYLINE_COLORS: string[] = [
  '#FFD700', // Dorado
  '#FF4444', // Rojo
  '#44FF44', // Verde
  '#4444FF', // Azul
  '#FF44FF', // Magenta
  '#44FFFF', // Cyan
  '#FFA500', // Naranja
  '#9944FF', // Púrpura
  '#FF6B6B', // Coral
  '#6BFFB8', // Menta
]

// Configuración del juego
export const REELS_COUNT = 5
export const ROWS_COUNT = 3
export const MIN_PAYLINE = 1
export const MAX_PAYLINE = 10
export const REEL_STRIP_LENGTH = 30 // Símbolos por carrete virtual

// Genera una tira de carrete con símbolos ponderados
export function generateReelStrip(): SlotSymbol[] {
  const strip: SlotSymbol[] = []
  const totalWeight = Object.values(SYMBOL_WEIGHTS).reduce((a, b) => a + b, 0)

  for (let i = 0; i < REEL_STRIP_LENGTH; i++) {
    let random = Math.random() * totalWeight
    for (const [symbol, weight] of Object.entries(SYMBOL_WEIGHTS)) {
      random -= weight
      if (random <= 0) {
        strip.push(symbol as SlotSymbol)
        break
      }
    }
  }

  return strip
}

// Genera los 5 carretes con sus tiras
export function generateReels(): SlotSymbol[][] {
  return Array.from({ length: REELS_COUNT }, () => generateReelStrip())
}

// Obtiene los símbolos visibles (matriz 5x3) de un spin
export function spinReels(reels: SlotSymbol[][]): SlotSymbol[][] {
  return reels.map(reel => {
    // Posición aleatoria en la tira
    const startPos = Math.floor(Math.random() * (reel.length - ROWS_COUNT))
    return [reel[startPos], reel[startPos + 1], reel[startPos + 2]]
  })
}

// Cuenta los scatters en toda la pantalla (no necesitan estar en línea)
export function countScatters(visibleSymbols: SlotSymbol[][]): number {
  let count = 0
  for (const reel of visibleSymbols) {
    for (const symbol of reel) {
      if (symbol === 'scatter') count++
    }
  }
  return count
}

// Obtiene la cantidad de free spins según scatters
export function getFreeSpins(scatterCount: number): number {
  return FREE_SPINS_TABLE[scatterCount] || 0
}

// Interfaz para resultados de línea ganadora
export interface WinResult {
  paylineIndex: number
  symbol: SlotSymbol
  count: number
  payout: number
  positions: [number, number][]
}

// Verifica una línea de pago y retorna el resultado si hay ganancia
export function checkPayline(
  visibleSymbols: SlotSymbol[][],
  paylineIndex: number,
  betPerLine: number
): WinResult | null {
  const payline = PAYLINES[paylineIndex]
  const symbolsInLine: SlotSymbol[] = payline.map(([reel, row]) => visibleSymbols[reel][row])

  // El primer símbolo define la combinación (o wild)
  let matchSymbol = symbolsInLine[0]
  const isWildStart = matchSymbol === 'wild'

  // Contar símbolos consecutivos desde la izquierda
  let count = 1
  const winningPositions: [number, number][] = [payline[0]]

  for (let i = 1; i < symbolsInLine.length; i++) {
    const current = symbolsInLine[i]

    // Si empezamos con wild, el primer símbolo no-wild define la combinación
    if (isWildStart && matchSymbol === 'wild' && current !== 'wild' && current !== 'scatter') {
      matchSymbol = current
    }

    // Wild sustituye cualquier símbolo excepto scatter
    if (current === matchSymbol || (current === 'wild' && matchSymbol !== 'scatter')) {
      count++
      winningPositions.push(payline[i])
    } else if (matchSymbol === 'wild' && current !== 'scatter') {
      // Si todo eran wilds y ahora hay otro símbolo, ese define la combinación
      matchSymbol = current
      count++
      winningPositions.push(payline[i])
    } else {
      break
    }
  }

  // Mínimo 3 símbolos para ganar
  if (count < 3) return null

  // Calcular pago
  const multiplier = PAY_TABLE[matchSymbol][count - 1]
  if (multiplier === 0) return null

  return {
    paylineIndex,
    symbol: matchSymbol,
    count,
    payout: multiplier * betPerLine,
    positions: winningPositions,
  }
}

// Verifica todas las líneas activas y retorna los resultados
export function checkAllPaylines(
  visibleSymbols: SlotSymbol[][],
  activePaylines: number,
  betPerLine: number
): WinResult[] {
  const results: WinResult[] = []

  for (let i = 0; i < activePaylines; i++) {
    const result = checkPayline(visibleSymbols, i, betPerLine)
    if (result) results.push(result)
  }

  return results
}

// Calcula el pago total de todas las ganancias
export function calculateTotalWin(wins: WinResult[]): number {
  return wins.reduce((total, win) => total + win.payout, 0)
}

// Obtiene todas las posiciones ganadoras únicas
export function getWinningPositions(wins: WinResult[]): [number, number][] {
  const posSet = new Set<string>()
  const positions: [number, number][] = []

  for (const win of wins) {
    for (const pos of win.positions) {
      const key = `${pos[0]}-${pos[1]}`
      if (!posSet.has(key)) {
        posSet.add(key)
        positions.push(pos)
      }
    }
  }

  return positions
}

// Genera símbolos visibles para un spin completo
export function performSpin(): SlotSymbol[][] {
  const reels = generateReels()
  return spinReels(reels)
}
